import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BackLink } from "@/components/BackLink";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft, Users, Search, UserPlus, UserMinus, Loader2, AlertTriangle, Layers, Trophy,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const dobLabel = (dob) => {
  if (!dob) return null;
  const d = new Date(dob);
  if (Number.isNaN(d.getTime())) return String(dob);
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

export default function CoachSquadBuilder() {
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  const [bucketId, setBucketId] = useState("");
  const [ageGroup, setAgeGroup] = useState("all");
  const [targetSquad, setTargetSquad] = useState("");
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async (opts = {}) => {
    const bucket = opts.bucketId !== undefined ? opts.bucketId : bucketId;
    const q = opts.search !== undefined ? opts.search : search;
    const group = opts.ageGroup !== undefined ? opts.ageGroup : ageGroup;
    try {
      const res = await axios.get(`${API}/academy/coach/squad-pool`, {
        params: {
          search: q,
          bucket_id: bucket || "",
          age_group: group === "all" ? "" : group,
        },
        withCredentials: true,
      });
      setData(res.data);
      if (!targetSquad && res.data.squads?.length) setTargetSquad(res.data.squads[0].id);
      // Show one bucket at a time: default to the first rather than everyone
      if (!bucket && res.data.buckets?.length) {
        const first = res.data.buckets[0].id;
        setBucketId(first);
        load({ bucketId: first, search: q, ageGroup: group });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load your squads");
      setData({ squads: [], pool: [], no_teams: true });
    }
    // eslint-disable-next-line
  }, [bucketId, search, ageGroup, targetSquad]);

  useEffect(() => { load(); }, []); // eslint-disable-line

  const assign = async (player, allowPlayDown = false) => {
    if (!targetSquad) {
      toast.error("Pick the squad to add them to");
      return;
    }
    const squad = data.squads.find((s) => s.id === targetSquad);
    if (!allowPlayDown &&
        !window.confirm(`Add ${player.name}${player.date_of_birth ? ` (born ${dobLabel(player.date_of_birth)})` : ""}` +
                        `${player.parent_name ? `, parent ${player.parent_name}` : ""} to ${squad?.name}?`)) {
      return;
    }
    setBusyId(player.id);
    try {
      const res = await axios.post(
        `${API}/academy/coach/assign-player-to-team`,
        { player_id: player.id, team_id: targetSquad, allow_play_down: allowPlayDown },
        { withCredentials: true }
      );
      toast.success(res.data.message + (res.data.playing_up ? " · playing up" : ""));
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not add the player";
      if (detail.includes("play UP") && !allowPlayDown) {
        if (window.confirm(`${detail}\n\nAdd them anyway?`)) {
          await assign(player, true);
          return;
        }
      } else {
        toast.error(detail);
      }
    } finally {
      setBusyId(null);
    }
  };

  const returnToPool = async (player) => {
    setBusyId(player.id);
    try {
      const res = await axios.post(
        `${API}/academy/coach/return-player-to-pool`,
        { player_id: player.id },
        { withCredentials: true }
      );
      toast.success(res.data.message);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not move the player");
    } finally {
      setBusyId(null);
    }
  };

  if (!data) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-emerald-400" />
      </div>
    );
  }

  const selectedBucket = (data.buckets || []).find((b) => b.id === bucketId);

  return (
    <div className="min-h-screen bg-slate-950 text-white" data-testid="coach-squad-builder">
      <div className="max-w-3xl mx-auto px-4 py-6 sm:px-6 sm:py-8 space-y-6">
        <BackLink to="/academy/coach-dashboard" label="Back"
          className="text-slate-300 hover:text-white -ml-2"
          testId="squad-builder-back" />

        <header>
          <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
            <Layers className="w-6 h-6 text-emerald-400" /> Squads &amp; buckets
          </h1>
          <p className="text-sm text-slate-400 mt-2">
            New sign-ups land in the age-group bucket. Pick one bucket at a time, then move
            players into one of your squads — you can send them back any time.
          </p>
        </header>

        {data.no_teams ? (
          <Card className="border-amber-500/30 bg-amber-500/5">
            <CardContent className="pt-5 flex items-start gap-3" data-testid="squad-builder-no-teams">
              <AlertTriangle className="w-5 h-5 text-amber-300 mt-0.5 shrink-0" />
              <p className="text-sm text-slate-200">
                You aren&apos;t assigned to a squad or bucket yet. Ask an academy admin to
                make you coach of one and this page will fill up.
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            {data.squads.length === 0 ? (
              <Card className="border-amber-500/30 bg-amber-500/5">
                <CardContent className="pt-5 text-sm text-slate-200" data-testid="squad-builder-no-squads">
                  You coach a bucket but have no squad to move players into yet — ask an admin
                  to create your squad (e.g. &quot;U10 Boys — Squad A&quot;).
                </CardContent>
              </Card>
            ) : (
              data.squads.map((sq) => (
                <Card key={sq.id} className="bg-slate-900/60 border-slate-800" data-testid={`squad-card-${sq.id}`}>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base md:text-lg flex flex-wrap items-center gap-2">
                      <Users className="w-5 h-5 text-emerald-400" /> {sq.name}
                      {sq.team_type === "league" && (
                        <Badge className="bg-amber-500/15 text-amber-300 border-amber-500/40 text-[10px]" data-testid={`squad-league-badge-${sq.id}`}>
                          <Trophy className="w-3 h-3 mr-1" />
                          {sq.league_name || "League team"}
                        </Badge>
                      )}
                      <span className="text-xs font-normal text-slate-500">
                        {sq.players.length} player{sq.players.length === 1 ? "" : "s"}
                      </span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {sq.players.length === 0 ? (
                      <p className="text-sm text-slate-500">Nobody here yet — add players from the pool below.</p>
                    ) : (
                      sq.players.map((p) => (
                        <div
                          key={p.id}
                          className="flex items-center justify-between gap-3 rounded-lg bg-slate-950/60 border border-slate-800 px-3 py-2"
                          data-testid={`squad-player-${p.id}`}
                        >
                          <div className="min-w-0">
                            <p className="text-sm text-white truncate">{p.name}</p>
                            <p className="text-xs text-slate-500 truncate">
                              {[p.age_group, p.jersey_number ? `#${p.jersey_number}` : null,
                                dobLabel(p.date_of_birth), p.parent_name]
                                .filter(Boolean).join(" · ")}
                            </p>
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-amber-300 hover:text-amber-200 min-h-[40px]"
                            onClick={() => returnToPool(p)}
                            disabled={busyId === p.id}
                            data-testid={`squad-remove-${p.id}`}
                          >
                            {busyId === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserMinus className="w-4 h-4" />}
                          </Button>
                        </div>
                      ))
                    )}
                  </CardContent>
                </Card>
              ))
            )}

            <Card className="bg-slate-900/60 border-slate-800" data-testid="squad-pool-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-base md:text-lg">
                  {selectedBucket ? selectedBucket.name : "Bucket pool"}
                  <span className="ml-2 text-xs font-normal text-slate-500">{data.pool.length} shown</span>
                </CardTitle>
                <p className="text-xs text-slate-500">
                  Only this bucket is listed, so you can&apos;t pick a child from another age group by mistake.
                </p>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid gap-2 sm:grid-cols-2">
                  <Select
                    value={bucketId}
                    onValueChange={(v) => { setBucketId(v); load({ bucketId: v }); }}
                  >
                    <SelectTrigger className="h-11" data-testid="squad-bucket-select">
                      <SelectValue placeholder="Which bucket?" />
                    </SelectTrigger>
                    <SelectContent>
                      {(data.buckets || []).map((b) => (
                        <SelectItem key={b.id} value={b.id}>
                          {b.name}{b.count != null ? ` (${b.count})` : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select
                    value={ageGroup}
                    onValueChange={(v) => { setAgeGroup(v); load({ ageGroup: v }); }}
                  >
                    <SelectTrigger className="h-11" data-testid="squad-age-filter">
                      <SelectValue placeholder="Age group" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All age groups in this bucket</SelectItem>
                      {(data.age_groups || []).map((g) => (
                        <SelectItem key={g} value={g}>{g}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex flex-col sm:flex-row gap-2">
                  <div className="relative flex-1">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                    <Input
                      className="pl-9 h-11"
                      placeholder="Search this bucket…"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && load({ search })}
                      data-testid="squad-pool-search"
                    />
                  </div>
                  <Button className="h-11 bg-emerald-600 hover:bg-emerald-500" onClick={() => load({ search })} data-testid="squad-pool-search-btn">
                    Search
                  </Button>
                </div>

                {data.squads.length > 1 && (
                  <Select value={targetSquad} onValueChange={setTargetSquad}>
                    <SelectTrigger className="h-11" data-testid="squad-target-select">
                      <SelectValue placeholder="Add to which squad?" />
                    </SelectTrigger>
                    <SelectContent>
                      {data.squads.map((sq) => (
                        <SelectItem key={sq.id} value={sq.id}>{sq.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}

                {data.pool.length === 0 ? (
                  <p className="text-sm text-slate-500" data-testid="squad-pool-empty">
                    Nobody waiting in this bucket.
                  </p>
                ) : (
                  <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
                    {data.pool.map((p) => (
                      <div
                        key={p.id}
                        className="flex items-center justify-between gap-3 rounded-lg bg-slate-950/60 border border-slate-800 px-3 py-2"
                        data-testid={`pool-player-${p.id}`}
                      >
                        <div className="min-w-0">
                          <p className="text-sm text-white truncate">{p.name}</p>
                          <div className="flex flex-wrap items-center gap-2 mt-0.5">
                            <Badge variant="outline" className="border-slate-700 text-slate-400 text-[10px]">
                              {p.age_group || "age ?"}
                            </Badge>
                            {p.date_of_birth && (
                              <span className="text-[11px] text-slate-500" data-testid={`pool-dob-${p.id}`}>
                                born {dobLabel(p.date_of_birth)}
                              </span>
                            )}
                            {p.parent_name ? (
                              <span className="text-[11px] text-slate-500 truncate" data-testid={`pool-parent-${p.id}`}>
                                {p.parent_name}
                              </span>
                            ) : (
                              <span className="text-[11px] text-slate-600 italic" data-testid={`pool-parent-${p.id}`}>
                                no parent on file
                              </span>
                            )}
                          </div>
                        </div>
                        <Button
                          size="sm"
                          className="bg-emerald-600 hover:bg-emerald-500 min-h-[40px] shrink-0"
                          onClick={() => assign(p)}
                          disabled={busyId === p.id || data.squads.length === 0}
                          data-testid={`pool-assign-${p.id}`}
                        >
                          {busyId === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
                          <span className="ml-1 hidden sm:inline">Add</span>
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}
